import type { SisBundleStatus } from "@canvas-sync/shared";

/**
 * Visual mapping for SIS bundle lifecycle states. Reuses the same badge
 * palette as runs/uploads (queued/running/success/warn/failed/cancelled)
 * to keep the dashboard's color vocabulary stable across surfaces.
 *
 * Lives in a non-component module so `SisBundleStatusBadge.tsx` can export
 * only its component (keeps React Fast Refresh happy).
 */
export const variants: Record<
  SisBundleStatus,
  { className: string; label: string; pulse: boolean }
> = {
  RECEIVED: { className: "badge badge-queued", label: "Received", pulse: true },
  VALIDATING: {
    className: "badge badge-running",
    label: "Validating",
    pulse: true,
  },
  INCOMPLETE_INPUTS: {
    className: "badge badge-failed",
    label: "Incomplete inputs",
    pulse: false,
  },
  INVALID_INPUTS: {
    className: "badge badge-failed",
    label: "Invalid inputs",
    pulse: false,
  },
  WAITING_FOR_GHOST_DETECTION: {
    className: "badge badge-running",
    label: "Waiting for ghost detection",
    pulse: true,
  },
  BUNDLING: {
    className: "badge badge-running",
    label: "Bundling",
    pulse: true,
  },
  POSTING: {
    className: "badge badge-running",
    label: "Posting to Canvas",
    pulse: true,
  },
  DRY_RUN_BUILT: {
    className: "badge badge-cancelled",
    label: "Dry run built",
    pulse: false,
  },
  SKIPPED_UNCHANGED: {
    className: "badge badge-cancelled",
    label: "Skipped (unchanged)",
    pulse: false,
  },
  SKIPPED_GHOST_DETECTION_TIMEOUT: {
    className: "badge badge-warn",
    label: "Ghost detection timed out",
    pulse: false,
  },
  // GHOST_FIX_ONLY mode with nothing safe to send — usually the happy
  // "no ghosts found" case; the detail page shows the exact reason.
  SKIPPED_NO_FIX: {
    className: "badge badge-cancelled",
    label: "Nothing to send (no ghost fix)",
    pulse: false,
  },
  SUBMITTED: {
    className: "badge badge-success",
    label: "Submitted",
    pulse: false,
  },
  SUBMITTED_SIS_ONLY: {
    className: "badge badge-warn",
    label: "Submitted (SIS only)",
    pulse: false,
  },
  // GHOST_FIX_ONLY mode's intended outcome: the ghost-fix CSV shipped
  // without the 5 SIS files.
  SUBMITTED_FIX_ONLY: {
    className: "badge badge-success",
    label: "Submitted (ghost fix only)",
    pulse: false,
  },
  FAILED: { className: "badge badge-failed", label: "Failed", pulse: false },
};
